import {customElement, property, query, state} from "lit/decorators.js";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {CalendarEntry} from "./calendarEntry";
import {EopImageSpinner, type EopOverlaySpinner} from "../../../page/elements/spinner";
import {schedule} from "../../../common/utils/promises";
import {FeedService} from "../feedService";
import {resolve} from "../../../container";
import Styles from "./calendarEntriesGrid.lit.scss";
import {CalendarRequest} from "../requests";
import {ELEMENT_SCROLL_PADDING, ScrollService} from "../../../common/scroll";
import {ANIMATION_DURATION} from "../../../common/gridLayout";
import {Timeout} from "../../../common/timeout";
import type {EopCalendarEntryTile} from "./calendarEntryTile";
import {MultiRowFeedPreviewNarrowSize, MultiRowFeedPreviewSize} from "../feedpreview/feedPreviewSize";
import {Resolution} from "../../../common/resolution";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {Dictionary} from "../../../page/elements/dictionary";

@customElement("eop-calendar-entries-grid")
export class EopCalendarEntriesGrid extends LitElement {

    public static readonly styles = Styles;

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "collections"})
    public collections: string = "";
    @property({attribute: "character-limit", type: Number})
    public maxDescriptionLength: number = 180;
    @property({attribute: "size", type: String})
    public size: Maybe<string> = undefined;
    @property({attribute: "use-page-overlay", type: Boolean})
    public usePageOverlay: boolean = false;
    @property({attribute: "show-more", type: Boolean})
    public enableMore: boolean = false;
    @property({attribute: "layout", reflect: true})
    public layout: string = "";

    @state()
    private entries: CalendarEntry[] = [];
    @state()
    private limit: number;

    @query("eop-image-spinner")
    private spinner: EopImageSpinner;
    @query("eop-overlay-spinner")
    private fetchMoreSpinner: EopOverlaySpinner;
    @query(".calendar-tiles")
    private calendarTilesContainer: HTMLElement;

    private totalCount: number = 0;
    private dictionary: Dictionary;

    public constructor(
        private feedService: FeedService = resolve(FeedService),
        private resolution: Resolution = resolve(Resolution),
        private scrollService: ScrollService = resolve(ScrollService),
        private timeout: Timeout = resolve(Timeout)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);

        if (this.layout === "narrow") {
            this.limit = new MultiRowFeedPreviewNarrowSize(this.size, this.resolution).getTileAmount();
        } else {
            this.limit = new MultiRowFeedPreviewSize(this.size, this.resolution).getTileAmount();
        }
    }

    public render(): TemplateResult {
        return html`
            <eop-overlay-spinner></eop-overlay-spinner>
            <eop-image-spinner></eop-image-spinner>
            ${this.renderTiles()}
            ${this.renderFetchMoreButton()}
        `;
    }

    private renderTiles(): Nullable<TemplateResult> {
        if (this.entries.isEmpty()) {
            return null;
        }
        return html`
            <div class="calendar-tiles">
                ${(this.renderCalendarTiles())}
            </div>`;
    }

    private renderCalendarTiles(): TemplateResult[] {
        return this.entries.map(entry => html`
            <eop-calendar-entry-tile .entry=${entry} enable-mobile-condensed></eop-calendar-entry-tile>`);
    }

    private renderFetchMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("FETCH_MORE");
        if (this.showFetchMore()) {
            return html`
                <div class="fetch-more-button">
                    <button type="button" class="primary" @click=${this.fetchMoreHandler} ${trackingButtonInteract(fetchMoreLabel)}>
                        ${fetchMoreLabel}
                    </button>
                </div>
            `;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        void schedule(this.spinner.spinWhile(this.fetchInitial()))
            .as("fetch");
    }

    protected fetchMoreHandler(): void {
        const offset = this.entries.length;
        void schedule(this.fetchMoreSpinner.spinWhile(this.fetchMore(offset)))
            .as("more");
    }

    private async fetchInitial(): Promise<void> {
        this.entries = await this.loadEntries(0);
    }

    private async fetchMore(offset: number): Promise<void> {
        const newEntries = await this.loadEntries(offset);
        this.entries = this.entries.slice(0, offset).concat(newEntries);
        this.scrollToNextElement(offset);
    }

    private showFetchMore(): boolean {
        return this.enableMore
            && this.totalCount > this.entries.length;
    }

    private scrollToNextElement(offset: number): void {
        void this.timeout.delay(() => {
            const nextTile = this.renderRoot.querySelectorAll<EopCalendarEntryTile>("eop-calendar-entry-tile").item(offset);
            void this.scrollService.scrollToElement(this.calendarTilesContainer, -nextTile.offsetTop + ELEMENT_SCROLL_PADDING, ANIMATION_DURATION);
        });
    }

    private async loadEntries(offset: number): Promise<CalendarEntry[]> {
        const request = new CalendarRequest(this.language, this.limit, offset, this.collections.parseCommaSeparated());
        const response = await this.feedService.calendarEntries(request);
        this.totalCount = response.totalCount;
        return response.entries.map(it => new CalendarEntry(it, this.maxDescriptionLength, this.usePageOverlay));
    }
}